import type { ReactNode } from "react";
import { ChevronLeft, ChevronRight, Filter, Grid3X3, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface HeaderSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  inputClassName?: string;
  type?: string;
}

export function HeaderSearchInput({
  value,
  onChange,
  placeholder,
  disabled,
  inputClassName,
  type = "text",
}: HeaderSearchInputProps) {
  return (
    <Input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={cn("search-input", inputClassName)}
    />
  );
}

interface HeaderTabItem {
  id: string;
  label: ReactNode;
  active: boolean;
  onClick: () => void;
  onMouseEnter?: () => void;
  disabled?: boolean;
}

interface HeaderTabNavProps {
  items: HeaderTabItem[];
  className?: string;
}

export function HeaderTabNav({ items, className }: HeaderTabNavProps) {
  return (
    <div className={cn("tab-navigation-container", className)}>
      <nav className="tab-navigation">
        {items.map((item) => {
          const widthReserveLabel =
            typeof item.label === "string" || typeof item.label === "number"
              ? String(item.label)
              : undefined;

          return (
            <button
              key={item.id}
              type="button"
              onClick={item.onClick}
              onMouseEnter={item.onMouseEnter}
              disabled={item.disabled}
              style={{ fontSize: "2.75em" }}
              data-label={widthReserveLabel}
              className={cn(
                "tab-nav-button",
                item.active ? "tab-nav-button-active" : "tab-nav-button-inactive",
                item.disabled && "cursor-not-allowed opacity-50"
              )}
            >
              <span className="tab-nav-button-label">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

interface HeaderActionGroupProps {
  children: ReactNode;
  className?: string;
}

export function HeaderActionGroup({ children, className }: HeaderActionGroupProps) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      {children}
    </div>
  );
}

interface CalendarHeaderControlsProps {
  viewMode: "calendar" | "list";
  onViewModeChange: (mode: "calendar" | "list") => void;
  onPrev: () => void;
  onNext: () => void;
  showFilter: boolean;
  onToggleFilter: () => void;
  onToday: () => void;
  disableToday?: boolean;
  className?: string;
  showTodayButton?: boolean;
  todayLabel?: string;
  hideNavArrows?: boolean;
}

export function CalendarHeaderControls({
  viewMode,
  onViewModeChange,
  onPrev,
  onNext,
  showFilter,
  onToggleFilter,
  onToday,
  disableToday,
  className,
  showTodayButton = true,
  todayLabel,
  hideNavArrows = false,
}: CalendarHeaderControlsProps) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="flex">
        {!hideNavArrows && (
          <Button variant="pillOutline" size="sm" onClick={onPrev} className="bg-white hover:bg-white">
            <ChevronLeft className="stroke-2" />
          </Button>
        )}
        {showTodayButton && (
          <Button
            variant="pillOutline"
            size="sm"
            onClick={onToday}
            disabled={disableToday}
            className={cn(
              disableToday ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-white text-slate-600",
              "hover:bg-white"
            )}
          >
            {todayLabel}
          </Button>
        )}
        {!hideNavArrows && (
          <Button variant="pillOutline" size="sm" onClick={onNext} className="bg-white hover:bg-white">
            <ChevronRight className="stroke-2" />
          </Button>
        )}
      </div>

      <Button
        variant="pillOutline"
        size="sm"
        onClick={onToggleFilter}
        className={cn(
          showFilter ? "bg-slate-100 text-slate-900" : "bg-white text-slate-600",
          "hover:bg-white"
        )}
      >
        <Filter />
      </Button>

      <div className="flex">
        <Button
          variant="pillOutline"
          size="sm"
          onClick={() => onViewModeChange("calendar")}
          className={cn(
            viewMode === "calendar" ? "bg-slate-100 text-slate-900" : "bg-white text-slate-600",
            "hover:bg-white"
          )}
        >
          <Grid3X3 />
        </Button>
        <Button
          variant="pillOutline"
          size="sm"
          onClick={() => onViewModeChange("list")}
          className={cn(
            viewMode === "list" ? "bg-slate-100 text-slate-900" : "bg-white text-slate-600",
            "hover:bg-white"
          )}
        >
          <List />
        </Button>
      </div>
    </div>
  );
}
